import { ErrorMessage, PageContainer, Spinner } from "@/components";
import InterviewCard from "@/components/interviews/InterviewCard";
import { useApplicationsQuery } from "@/hooks/api/application.queries";
import { useInterviewByIdQuery } from "@/hooks/api/interview.queries";
import { isNotFoundError } from "@/services/interview.service";
import { ArrowLeft, CalendarX2 } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";

export default function InterviewView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: interview, isLoading, isError, error, refetch } =
    useInterviewByIdQuery(id);

  // Shares a cache key with the list page, so this is usually already resolved.
  const { data: applicationsData } = useApplicationsQuery({ page: 1, limit: 100 });

  const backLink = (
    <button
      onClick={() => navigate("/interviews")}
      className="mb-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 transition hover:text-indigo-600"
    >
      <ArrowLeft size={15} /> Back to interviews
    </button>
  );

  if (isLoading) {
    return (
      <PageContainer className="min-h-screen">
        <div className="flex flex-col items-center justify-center gap-4 py-32">
          <Spinner />
          <p className="animate-pulse text-sm text-gray-400">
            Loading interview...
          </p>
        </div>
      </PageContainer>
    );
  }

  // A 404 covers both "deleted" and "belongs to another student" — the student
  // is shown the same thing either way, which is what the API intends.
  if (isError && isNotFoundError(error)) {
    return (
      <PageContainer className="min-h-screen">
        {backLink}
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white py-32 text-center">
          <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-slate-50">
            <CalendarX2 size={32} className="text-slate-300" />
          </div>
          <h3 className="mb-2 text-xl font-bold text-gray-700">
            This interview is no longer available
          </h3>
          <p className="mb-6 max-w-xs text-sm text-gray-400">
            It may have been removed, or the link points somewhere you do not
            have access to.
          </p>
          <Link
            to="/interviews"
            className="rounded-xl bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Back to My Interviews
          </Link>
        </div>
      </PageContainer>
    );
  }

  if (isError || !interview) {
    return (
      <PageContainer className="min-h-screen">
        {backLink}
        <ErrorMessage
          message="Failed to load this interview."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  const applicationStatus = applicationsData?.items?.find(
    (application) => application.id === interview.application_id
  )?.status;

  return (
    <PageContainer className="min-h-screen">
      {backLink}
      <div className="max-w-2xl">
        <InterviewCard
          interview={interview}
          applicationStatus={applicationStatus}
          showDetailLink={false}
        />
      </div>
    </PageContainer>
  );
}
